import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import styles from '../styles.module.scss';
import { OccurrencePrice } from '../components/OccurrencePrice';
import DeliveryIndicator from '../../../DeliveryIndicator';
import { OccurrenceExtras } from '../components/OccurrenceExtras';
import { OccurrenceButton } from '../components/OccurrenceButton';
import OccurrenceBlockName from './components/OccurrenceBlockName';
import { ChildOccurrences } from './components/ChildOccurrences';
import { OccurrenceBlockDescription } from './components/OccurrenceBlockDescription';
import { OccurrenceBlockDates } from './components/OccurrenceBlockDates';
import {
    PublicBlendedCourse,
    PublicBlockOccurrence,
    PublicChildOccurrence,
    PublicClassCourse,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { isChildOccurrence } from '../../../../services/occurrences/isChildOccurrence';
import { VatSettings } from '@shared/types';

type OccurrenceBlockCardProps = {
    course: PublicClassCourse | PublicBlendedCourse;
    hidePricing?: boolean;
    isSoldOut: boolean;
    isWaitlistEnabled: boolean;
    occurrence: PublicBlockOccurrence;
    occurrenceCardClickHandler: (selectedOccurrence: PublicOccurrence) => void;
    occurrences: Array<PublicOccurrence>;
    vatSettings?: VatSettings;
};

export const OccurrenceBlockCard: FunctionComponent<OccurrenceBlockCardProps> = ({
    course,
    hidePricing,
    isSoldOut,
    isWaitlistEnabled,
    occurrence,
    occurrenceCardClickHandler,
    occurrences,
    vatSettings,
}) => {
    const childOccurrences = useMemo<PublicChildOccurrence[]>(() => {
        return occurrences.filter(
            (_occurrence): _occurrence is PublicChildOccurrence =>
                isChildOccurrence(_occurrence) && occurrence._id === _occurrence.parent_ref
        );
    }, [occurrence, occurrences]);
    const isApplication: boolean = useMemo(() => {
        return course.application_required === true;
    }, [course]);

    const bookableChildOccurrences = useMemo<PublicChildOccurrence[]>(
        () => childOccurrences.filter((occurrence) => occurrence.individually_bookable),
        [childOccurrences]
    );

    const [open, setOpen] = useState<boolean>(bookableChildOccurrences.length > 0);

    const handleBlockCardOpen = useCallback(() => {
        setOpen(!open);
    }, [open]);

    const handleBlockCardButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            occurrenceCardClickHandler(occurrence);
        },
        [occurrence, occurrenceCardClickHandler]
    );

    return (
        <li
            className={
                isSoldOut && !isWaitlistEnabled
                    ? styles.occurrenceBlockCardDisabled
                    : styles.occurrenceBlockCard
            }
            onClick={handleBlockCardOpen}
            data-testid="occurrence-block-card"
        >
            <div className={styles.blockOccurrenceDetails}>
                <div className={styles.nameAndDates}>
                    <OccurrenceBlockName occurrence={occurrence} />
                    <OccurrenceBlockDates
                        childOccurrences={childOccurrences}
                        occurrence={occurrence}
                    />
                    <OccurrenceExtras
                        occurrence={occurrence}
                        isChild={false}
                        isBlock={true}
                        childOccurrences={[]}
                        open={open}
                        setOpen={setOpen}
                    />
                </div>
                <div className={styles.deliveryAndOccurrenceExtras}>
                    <DeliveryIndicator
                        delivery={occurrence.delivery || course.delivery}
                        locations={[occurrence?.address?.city || course.address?.city]}
                    />
                </div>
                <OccurrencePrice
                    occurrence={occurrence}
                    course={course}
                    hidePricing={hidePricing}
                    isBookable={true}
                    vatSettings={vatSettings}
                />
                <OccurrenceButton
                    isSoldOut={isSoldOut}
                    isWaitlistEnabled={isWaitlistEnabled}
                    isBookable={true}
                    isBlock={true}
                    isApplication={isApplication}
                    hasBookableChildOccurrences={bookableChildOccurrences.length > 0}
                    onClick={handleBlockCardButtonClick}
                />
                <OccurrenceBlockDescription
                    occurrenceBlockDescription={occurrence.block_description}
                />
            </div>
            <ChildOccurrences
                childOccurrences={childOccurrences}
                course={course}
                occurrenceCardClickHandler={occurrenceCardClickHandler}
                isSoldOut={isSoldOut}
                isWaitlistEnabled={isWaitlistEnabled}
                open={open}
                setOpen={setOpen}
                hidePricing={hidePricing}
                vatSettings={vatSettings}
                blockOccurrence={occurrence}
            />
        </li>
    );
};
